import { Button, DataGrid, Tooltip } from 'devextreme-react';
import { Column, Item, Paging, Toolbar, FilterRow, Pager, Button as Butt, LoadPanel } from 'devextreme-react/data-grid';
import {filter, resButtonRefresh } from '../../images/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { useAppSelector } from '../../contexts/hooks';
import { newToast } from '../../components';
import moment from 'moment';
import { FilterComponent } from '../find-results/Modals/FilterOrders';
import { PatientDetails } from './Modals/PatientDetails';
import { useApiSearch } from '../../api/api.Search';

const initialFilters = {
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
};

const Index = () => {
  const gridRef = useRef<DataGrid>(null);
  const [modalFiltersVisible, setModalFiltersVisible] = useState(true);
  const [modalPatientDetailsVisible, setModalPatientDetailsVisible] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [currentFilters, setCurrentFilters] = useState(initialFilters);
  const [data, setData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const { SearchPatient } = useApiSearch();

  const info = useAppSelector((x) => x.configuration.Data?.SystemFields)
    ?.filter((x) => x.Dfc_Tabla === 'Pacientes' && x.Dfc_Estado === true)
    .sort((a, b) => a.Dfc_Orden - b.Dfc_Orden);

  const dateFormat = useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'DateTimeClientFormat')[0].Cgn_Resultado);

  // Función para obtener el caption correcto de forma completamente dinámica
  const getColumnCaption = useCallback(
    (fieldName: string, configLabel?: string) => {
      // 1. Si hay configuración con label, usarlo en mayúsculas (prioridad a la configuración del servidor)
      if (configLabel) {
        return configLabel.toUpperCase();
      }

      // 2. Conversión automática como fallback si no hay configuración
      return fieldName
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/[_.-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
    },
    []
  );

  // Función para generar columnas dinámicamente basándose en los datos de la API
  const generateColumns = useCallback(
    (apiData: any[]) => {
      if (!apiData || apiData.length === 0) return [];

      const firstRow = apiData[0];
      const generatedColumns: any[] = [];

      // Obtener todas las claves del primer objeto de la respuesta API
      Object.keys(firstRow).forEach((apiFieldName) => {
        // Pc_Id no debe mostrarse nunca
        if (apiFieldName === 'Pc_Id') {
          return;
        }

        // Pc_Identificacion SIEMPRE viene y debe estar de primera (hardcodeado)
        if (apiFieldName === 'Pc_Identificacion') {
          generatedColumns.push({
            dataField: apiFieldName,
            caption: 'IDENTIFICACIÓN',
            alignment: 'center',
            visible: true,
            order: -1, // Para que aparezca primero SIEMPRE
            allowResizing: false, // No permitir redimensionar
          });
          return;
        }

        // Para el resto de campos, buscar la configuración de campo que coincida exactamente con el nombre del campo de la API
        const fieldConfig = info?.find((field) => field.Dfc_Equivalencia === apiFieldName);

        if (fieldConfig && fieldConfig.Dfc_Visible) {
          // Campo mapeado con configuración del servidor
          const column: any = {
            dataField: apiFieldName,
            caption: getColumnCaption(apiFieldName, fieldConfig.Dfc_Label),
            visible: true,
            order: fieldConfig.Dfc_Orden || 999,
          };

          // Configurar propiedades de columna basándose en el tipo de campo configurado
          if (fieldConfig.TipoCampo) {
            switch (fieldConfig.TipoCampo.Tcm_Equivalencia) {
              case 'dxDateBox':
                column.dataType = 'date';
                column.format = dateFormat?.toLowerCase() || 'dd/MM/yyyy';
                break;
              case 'dxTextBox':
                if (apiFieldName.toLowerCase().includes('identificacion') || apiFieldName.toLowerCase().includes('documento')) {
                  column.alignment = 'center';
                }
                break;
              case 'dxSelectBox':
                break;
              case 'dxCheckBox':
              case 'dxSwitch':
                column.alignment = 'center';
                break;
            }
          }

          generatedColumns.push(column);
        } else {
          // Campo NO mapeado - mostrar tal como viene de la API con conversión automática
          const column: any = {
            dataField: apiFieldName,
            caption: getColumnCaption(apiFieldName),
            visible: true,
            order: 1000, // Aparecer al final
          };

          // Aplicar algunas reglas básicas de inferencia según el nombre del campo
          if (apiFieldName.toLowerCase().includes('fecha') || apiFieldName.toLowerCase().includes('date')) {
            column.dataType = 'date';
            column.format = dateFormat?.toLowerCase() || 'dd/MM/yyyy';
          } else if (apiFieldName.toLowerCase().includes('id') || apiFieldName.toLowerCase().includes('identificacion')) {
            column.alignment = 'center';
          }

          // Verificar si el campo tiene caracteres problemáticos (puntos, espacios, etc.)
          if (apiFieldName.includes('.') || apiFieldName.includes(' ') || /[^a-zA-Z0-9_]/.test(apiFieldName)) {
            console.warn(`Campo con nombre problemático detectado: "${apiFieldName}"`);
            // Para campos con nombres problemáticos, agregar un cellRender personalizado
            column.cellRender = (cellData: any) => {
              return cellData.data[apiFieldName] || '';
            };
          }

          generatedColumns.push(column);
        }
      });

      // Ordenar las columnas: primero Pc_Identificacion, luego por orden de configuración, finalmente campos no mapeados
      generatedColumns.sort((a, b) => {
        return (a.order || 999) - (b.order || 999);
      });

      return generatedColumns;
    },
    [info, dateFormat, getColumnCaption]
  );

  // CustomStore para manejar los datos
  const customDataSrc = useMemo(() => {
    return new CustomStore({
      key: 'Pc_Id',
      load: () =>
        Promise.resolve({
          data: data,
          totalCount: data.length,
        }),
    });
  }, [data]);

  const customData = useMemo(() => {
    return new DataSource({
      store: customDataSrc,
      reshapeOnPush: true,
    });
  }, [customDataSrc]);

  const handleModalShowFilter = useCallback(() => {
    setModalFiltersVisible(true);
  }, []);

  const handleModalHideFilter = useCallback(() => {
    setModalFiltersVisible(false);
  }, []);

  const handleApplyFilters = useCallback(
    (filters: any) => {
      setCurrentFilters(filters);
      gridRef.current?.instance?.beginCustomLoading('Buscando pacientes...');

      SearchPatient(filters)
        .then((data) => {
          if (data.length === 0) {
            newToast('INFO', 'No se encontraron pacientes con los filtros aplicados', 3);
            setColumns([]);
          } else {
            // Generar columnas dinámicamente basándose en los datos
            const generatedColumns = generateColumns(data);
            setColumns(generatedColumns);
            newToast('SUCCESS', `Se encontraron ${data.length} paciente(s)`, 3);
          }
          setData(data);
          setHasSearched(true);
        })
        .catch((error) => {
          newToast('ERROR', 'Error al buscar pacientes: ' + error.message, 5);
          setHasSearched(true);
        })
        .finally(() => {
          gridRef.current?.instance?.endCustomLoading();
        });
      handleModalHideFilter();
    },
    [handleModalHideFilter, SearchPatient, generateColumns]
  );

  const handleRefreshData = useCallback(() => {
    if (hasSearched && Object.keys(currentFilters).length > 0) {
      handleApplyFilters(currentFilters);
    }
  }, [SearchPatient, currentFilters, hasSearched, handleApplyFilters]);

  const handleView = useCallback((data: any) => {    
    setSelectedPatient(data.row.data);
    setModalPatientDetailsVisible(true);    
  }, []);

  return (
    <div className={'view crm-contact-list'}>
      <div className={'view-wrapper view-wrapper-contact-list'}>
        <DataGrid className='grid' dataSource={customData} ref={gridRef} height='100%' width={'100%'} repaintChangesOnly={false} highlightChanges={false} hoverStateEnabled={true} rowAlternationEnabled={true} showColumnLines={true} allowColumnResizing={true} columnAutoWidth={true} noDataText={hasSearched ? 'No hay pacientes para mostrar' : 'Aplique filtros para buscar pacientes'}>
          <LoadPanel enabled={true} />
          <Paging enabled={true} />
          <FilterRow visible={true} applyFilter='auto' />
          <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>
                Búsqueda de pacientes
                {hasSearched && data.length > 0 && (
                  <span style={{ marginLeft: '10px', fontSize: '14px', color: '#666' }}>
                    ({data.length} paciente{data.length !== 1 ? 's' : ''} encontrado{data.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
            </Item>

            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='.filter-button' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Filtrar pacientes
              </Tooltip>
              <Button className='filter-button' icon={filter} text='Filtrar' stylingMode='text' onClick={handleModalShowFilter} />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Tooltip target='.refresh-button' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Refrescar datos
              </Tooltip>
              <Button className='refresh-button' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleRefreshData} disabled={!hasSearched} />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          {/* Mostrar columnas dinámicas solo si se ha realizado una búsqueda */}
          {hasSearched && columns.map((col, index) => <Column key={index} dataField={col.dataField} caption={col.caption} allowResizing={col.allowResizing} alignment={col.alignment} dataType={col.dataType} format={col.format} visible={col.visible} cellRender={col.cellRender} />)}

          {/* Columna de acción siempre visible si hay búsqueda */}
          {hasSearched && (
            <Column type='buttons' caption={'ACCIÓN'} width={80}>
              <Butt icon='eyeopen' text='Ver' onClick={handleView} />
            </Column>
          )}
        </DataGrid>
      </div>
      <FilterComponent onApplyFilters={handleApplyFilters} visible={modalFiltersVisible} setVisible={handleModalHideFilter} includeOrderFields={false} includePatientFields />
      <PatientDetails visible={modalPatientDetailsVisible} setVisible={setModalPatientDetailsVisible} patient={selectedPatient} />
    </div>
  );
};

export default Index;
